/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { Action } from '@src/app/model/action';
import { Field } from '@src/app/shared/field/Field';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { DateTimeField, FormDateTimeComponent } from '@src/app/shared/form/form-date-time/form-date-time.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormDurationComponent } from '@src/app/shared/form/form-duration/form-duration.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { required } from '@src/app/shared/validators';
import { UserService } from '@src/app/modules/user/user.service';
import { ActionService } from '@src/app/modules/crm/action.service';
import { AgentDataService } from '@src/app/modules/agent/agent-data.service';
import { User } from '@src/app/model/user';
import { Omc } from '@src/app/model/Omc';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { TrendField } from '@src/app/shared/field/TrendField';
import { ActionTemplatesService } from '@src/app/modules/crm/action-templates.service';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { ActionPageNavRoute } from '@src/app/routes/action-page-nav';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { FormRichTextComponent } from '@src/app/shared/form/form-rich-text/form-rich-text.component';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { ImageUploadRequestAction } from '@src/app/modules/crm/service-request-page/image-upload-request/image-upload-request.component';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { UnitPageNavRoute } from '@src/app/routes/unit-page';
import { PersonPageNavRoute } from '@src/app/routes/person-page-nav';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';
import { RequestTemplatePageNavRoute } from '@src/app/routes/request-template-page-nav';
import { first } from 'rxjs/operators';

@Component({
    selector: 'app-action-page',
    templateUrl: './action-page.component.html',
    styleUrls: ['./action-page.component.scss'],
    imports: [FormPageComponent]
})
export class ActionPageComponent extends AbstractPageComponent {
    dataSvc = inject(ActionService);
    private tmplService = inject(ActionTemplatesService);
    private cds = inject(ConfirmDialogService);
    private isNarrowSvc = inject(IsNarrowService);


    currentUser: User;
    users: User[] = [];
    omcs: Omc[] = [];

    omcFieldSearch = FormPicklistComponent.make('Owner Management Company', 'omcId', 'omc',
        { items: this.omcs }, { readonly: true, visible: Field.noShow }
    );
    omcField = FormTextComponent.make('Owner Management Company', 'omcName', {readonly: true, allowFiltering: false});

    plannedField = FormPicklistComponent.make('Planned', 'planned', '',
        { items: [{ id: 0, name: 'No' }, { id: 1, name: 'Yes' }] },
        { visible: Field.noShow, calculateValue: (o) => (o as Action).plannedDate ? 'Yes' : 'No' }
    );

    doneField = FormPicklistComponent.make('Done', 'done', '',
        { items: [{ id: 0, name: 'No' }, { id: 1, name: 'Yes' }], blockFilterNone: true },
        { visible: Field.noShow, calculateValue: (o) => (o as Action).completedAt ? 'Yes' : 'No' }
    );

    titleField = FormTextComponent.make('Title', 'title', { validators: [required], cellOpts: { width: '25%' }, formColumn: 2 });

    createdAtField: DateTimeField = (FormDateTimeComponent.make('Created', 'createdAt',
        { condensed: true, visible: Field.formOnly, formColumn: 4, disable: true }) as DateTimeField
    );

    ownerReadAtField: DateTimeField = (FormDateTimeComponent.make('Owner Read', 'ownerReadAt',
        { condensed: true, visible: Field.formOnly, formColumn: 4, disable: true, sendServer: false }) as DateTimeField
    );

    createdByField = FormPicklistComponent.make('Created By', 'createdById', 'createdByName', {selfPopulate: true},
        { condensed: true, formColumn: 4, disable: true }
    );

    completedAtField: DateTimeField = (FormDateTimeComponent.make('Completed', 'completedAt',
        { visible: Field.formOnly, formColumn: 2, disable: true }) as DateTimeField
    );

    completedByField = FormPicklistComponent.make('Completed By', 'completedById', 'completedByName',
        { selfPopulate: true },
        { formColumn: 4, cellOpts: { minWidth: '60px' }, disable: true, condensed: true }
    );
    tableDoneField = FormButtonComponent.make('Done', 'completedAtTableShow', {
        type: 'icon',
        calculateValue: (action) => (action as Action).completedAt ? 'check_box_outline' : 'check_box_outline_blank',
        btnOpts: { tableClickMethod: this.markActionCompleteFromTable.bind(this)},
        visible: Field.noForm,
        allowFiltering: false,
    });

    formDoneField = FormButtonComponent.make('Done', 'completedAtFormShow', {
        type: 'icon',
        calculateValue: (action) => (action as Action).completedAt ? 'check_box_outline' : 'check_box_outline_blank',
        btnOpts: { clickMethod: this.markActionComplete.bind(this) },
        visible: Field.formOnly,
        formColumn: 1
    });

    propertyManagerField = FormPicklistComponent.make('Property Manager', 'omcManagerId', 'omcManagerName',
        { selfPopulate: true },
        { formColumn: 1, readonly: true, condensed: true, sendServer: false }
    ).override({formColumn: 4});

    templateField = FormPicklistComponent.make('Standard Action', 'templateId', null,
        { service: this.tmplService },
        { formColumn: 1, readonly: true, sendServer: false, visible: Field.noShow }
    );

    ownedByField = FormPicklistComponent.make('Owned By', 'ownedById', 'ownedBy', { items: this.users },
        { formColumn: 2, cellOpts: { minWidth: '60px' } }
    );

    plannedDateField = FormDateComponent.make('Planned Date', 'plannedDate', { formColumn: 2 });

    timeSpentField = FormDurationComponent.make('Time Spent', 'timeSpent', { formColumn: 1 });

    weekField = TrendField.make('Week Completed', 'weekCompleted', { cellOpts: { heading: 'Week' } });

    /* Hidden, so the request analysis by user can link to the actions completed in a week or month */
    workWeekField = FormTextComponent.make('Week Completed', 'workWeek', { visible: Field.noShow, sendServer: false, readonly: true });
    workMonthField = FormTextComponent.make('Month Completed', 'workMonth', { visible: Field.noShow, sendServer: false, readonly: true });

    requestField = FormNavLinkComponent.makeDetail('Request',
        (o: Action) => o.request ? (o.request?.refNr + ' ' + o.request?.title.trim()) : '',
        'requestId',
        ServiceRequestPageNavRoute,
        true
    ).override({formColumn: 4});

    templateLinkField = FormNavLinkComponent.makeDetail('requestTemplateId',
        (o:Action) => (o.templateId && o.request?.templateId ? o.template?.name : ''),
        'request.templateId',
        RequestTemplatePageNavRoute
    ).override({ label: 'Standard Request', formColumn: 4 });

    unitField = FormNavLinkComponent.makeDetail('Unit', 'unitName', 'unitId',
        UnitPageNavRoute, true
    ).override({formColumn: 4});

    personField = FormNavLinkComponent.makeDetail('Contact', 'personFullName', 'personId',
        PersonPageNavRoute, true
    ).override({formColumn: 4});

    descriptionField = FormRichTextComponent.make('Description', 'description', { cellOpts: { width: '50%' },
        formColumn: 3, visible: Field.formOnly
    })

    tmplInstructionField = FormTextAreaComponent.make('Action Instructions', 'template.description',
        {
            cellOpts: { width: '50%' }, formColumn: 3, disable: true,
            sendServer: false, visible: Field.formOnly
        }
    );

    config = new FormConfig({
        navRoute: ActionPageNavRoute,
        docPath: 'omc-community/managers/actions',
        title: $localize`ToDo Actions`,
        help: $localize`Priority for dealing with requests`,
        allowNew: false,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                FieldMaker.idHolder('teamId'),
                this.titleField,
                this.ownedByField,                
                this.plannedDateField,
                this.omcField,
                this.omcFieldSearch,
                this.requestField,
                this.personField,
                this.unitField,                

                this.timeSpentField,
                this.tableDoneField,
                this.formDoneField,
                this.doneField,

                this.descriptionField,
                this.tmplInstructionField,
                this.completedAtField,

                this.createdAtField,
                this.createdByField,
                this.ownerReadAtField,

                this.completedByField,
                this.plannedField,
                this.weekField,
                this.workWeekField,
                this.workMonthField,
                this.templateField,
                this.templateLinkField,
                this.propertyManagerField,
            ],
            formLayout: [{ cells: [{ width: '25%' }, { width: '25%' }, { width: '38%' }, { width: '12%' }] }],
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: () => of(new Action()),
        beforeEdit: this.beforeEdit.bind(this),
        configReady: this.configReady,
        tableHighlighter: (o) => (o as Action).ownerReadAt ? false : true,
        afterSave: this.afterSave.bind(this),
        actions: [new ImageUploadRequestAction(false)]
    });

    constructor() {
        const currentUserSvc = inject(CurrentUserService);
        const teamUserSvc = inject(UserService);
        const agentDataSvc = inject(AgentDataService);

        super();
        const isNarrowSvc = this.isNarrowSvc;

        isNarrowSvc.detectVeryNarrow().pipe(takeUntilDestroyed()).subscribe( isPhone => {
            this.createdAtField.visible = isPhone ? Field.noShow : Field.showAll;
            this.createdByField.visible = isPhone ? Field.noShow : Field.showAll;
            this.ownerReadAtField.visible = isPhone ? Field.noShow : Field.showAll;
            this.templateLinkField.visible = isPhone ? Field.noShow : Field.showAll;
            this.completedByField.visible = isPhone ? Field.noShow : Field.showAll;
            //this.tableDoneField.visible = isPhone ? Field.noShow : Field.formOnly;
        })

        forkJoin({
            currentUser: currentUserSvc.getCurrentUser().pipe(first()),
            users: teamUserSvc.getUsers().pipe(first()),
            omcs: agentDataSvc.getOmcList().pipe(first()),
        }).subscribe(result => {
            this.currentUser = result.currentUser;
            for (const o of result.omcs) {
                this.omcs.push(o as Omc);
            }
            for (const u of result.users as User[]) {
                this.users.push(u);
            }
            this.configReady.next(null);            
        })
    }

    afterSave() {
        if (this.isNarrowSvc.isPhone()) {
            history.back();
        }
    }
    beforeEdit(a: Action) {
        if (a.completedAt) {
            this.formDoneField.visible = Field.noShow;
            this.config.allowEdit = false;
            this.config.readonly = true;
        } else {
            this.formDoneField.visible = Field.formOnly;
            this.config.allowEdit = true;
            this.config.readonly = false;
        }
        if (!a.templateId) {
            this.templateLinkField.visible = Field.noShow;
            this.tmplInstructionField.visible = Field.noShow;
        } else {
            this.templateLinkField.visible = Field.formOnly;
            this.tmplInstructionField.visible = Field.formOnly;
        }
        return a;
    }

    markActionComplete() {
        this.cds.open('Confirm Completed',
            'Save action and mark completed? You cannot undo this action', () => {
                this.completedAtField.setToCurrentDateTime();
                this.completedByField.control.setValue(this.currentUser.id);
                this.page.form.doSaveChanges();
            });
    }
    
    markActionCompleteFromTable(action: Action) {
        if (!action.completedAt) {
            this.cds.open('Confirm Completed',
                'Mark this action completed? You cannot undo this action',
                () => {
                    this.dataSvc.complete(action, this.currentUser).subscribe(result => {
                        if (result) {
                            action.completedAt = result.completedAt;
                            action.completedById = result.completedById;
                        }
                    })
                }
            );
        } else {
            this.cds.alert('Cannot Mark Undone', 'Cannot undo completing an action, please add a comment to it or create a replacement action');
        }
    }

}
